import React, { createContext, useContext, useMemo, useRef } from 'react';

import {
  OFFICES_URL_PREFIX,
  SEARCH_BY_COUNTY_SUFFIX,
  SEARCH_QUERY_GENERIC,
} from '../../common/consts';
import { removeCounty } from '../../common/helpers';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../../common/helpers/buyHouseOption.utils';
import { getFlatTypeSlug } from '../../common/helpers/flatType.utils';
import {
  computeOfficesByCountyArray,
  generateUrl,
} from '../../common/helpers/office.utils';
import { computeSearchOptions } from '../../common/helpers/searchOption.utils';
import {
  getTitleFromQuery,
  slugifySearchQuery,
} from '../../common/helpers/searchQuery.utils';
import {
  computeSellHouseOptions,
  SellHouseOption,
} from '../../common/helpers/sellHouse.utils';
import useCountyCityZone from '../../common/hooks/useCountyCityZone';
import useI18n from '../../common/hooks/useI18n';
import { IFlat } from '../../common/model/flat.model';
import { IOffice } from '../../common/model/office.model';
import { SearchType } from '../../common/model/enums/searchType.enum';
import { I18nContextType } from '../../libs/i18n';

type LinksGridMode = 'home' | 'search' | 'sell';

type LinksGridProviderProps = {
  mode: LinksGridMode;
  flats: IFlat[];
  offices: IOffice[];
  children?: React.ReactNode;
};

type Link = {
  name: string;
  url: string;
};

type LinksGridContextValue = {
  mode: LinksGridMode;
  visible: boolean;
  linksMap: Map<string, Link[]>;
};

const LinksGridContext = createContext<LinksGridContextValue | undefined>(
  undefined
);

export const useLinksGrid = (): LinksGridContextValue => {
  const context = useContext(LinksGridContext);
  if (!context) {
    throw new Error('useLinksGrid must be used within a LinksGridProvider');
  }
  return context;
};

export const LinksGridProvider: React.FC<LinksGridProviderProps> = ({
  mode,
  flats,
  offices,
  children,
}) => {
  const i18n = useI18n();

  const searchOptionsRef = useRef(computeSearchOptions(flats));
  const sellHouseOptionsRef = useRef(
    computeSellHouseOptions(searchOptionsRef.current, true)
  );
  const buyHouseOptionsRef = useRef(
    computeBuyHouseOptions(searchOptionsRef.current, true).filter(
      (option) => option.query !== SEARCH_QUERY_GENERIC
    )
  );

  const [county, city, zone] = useCountyCityZone();

  const linksMap = useMemo(() => {
    const map = new Map<string, Link[]>();

    if (zone) {
      return map;
    }

    const buyHousesLinks = generateBuyHousesLinks(
      i18n,
      buyHouseOptionsRef.current,
      mode,
      county,
      city
    );
    if (buyHousesLinks.length > 0) {
      map.set(LinksGridCategory.BUY_HOUSE, buyHousesLinks);
    }

    const officesLinks = generateOfficesLinks(i18n, offices, county, city);
    if (officesLinks.length > 0) {
      map.set(LinksGridCategory.OFFICES, officesLinks);
    }

    const sellHouseLinks = generateSellHouseLinks(
      i18n,
      sellHouseOptionsRef.current,
      mode,
      county,
      city
    );
    if (sellHouseLinks.length > 0) {
      map.set(LinksGridCategory.SELL_HOUSE, sellHouseLinks);
    }

    map.forEach((links, _) => {
      links.forEach((link) => {
        link.name = removeCounty(link.name);
      });
    });

    return map;
  }, [city, county, i18n, mode, offices, zone]);

  const visible = useMemo(() => linksMap.size > 0, [linksMap]);

  const value = useMemo(() => ({ mode, visible, linksMap }), [
    mode,
    visible,
    linksMap,
  ]);

  return (
    <LinksGridContext.Provider value={value}>
      {linksMap.size > 0 && children}
    </LinksGridContext.Provider>
  );
};

export enum LinksGridCategory {
  BUY_HOUSE = 'buyHouse',
  OFFICES = 'offices.title',
  SELL_HOUSE = 'sellHouse.title',
}

export const getHrefByCategory = (category: string): string => {
  switch (category) {
    case LinksGridCategory.BUY_HOUSE:
      return '/comprar';
    case LinksGridCategory.OFFICES:
      return `/${OFFICES_URL_PREFIX}`;
    case LinksGridCategory.SELL_HOUSE:
      return '/vender-casa';
    default:
      return '/';
  }
};

const generateBuyHousesLinks = (
  i18n: I18nContextType,
  buyHouseOptions: BuyHouseOption[],
  mode: LinksGridMode,
  county?: string,
  city?: string
): Link[] => {
  const links = buyHouseOptions
    .filter((option) => {
      const splitQuery = option.query.split('/');
      if (city) {
        const minSplitQueryLength = mode === 'search' ? 3 : 2;
        return (
          splitQuery.length >= minSplitQueryLength &&
          splitQuery.length <= 3 &&
          splitQuery[1]?.includes(city)
        );
      }
      if (county) {
        const minSplitQueryLength = mode === 'search' ? 2 : 1;
        return (
          splitQuery.length >= minSplitQueryLength &&
          splitQuery.length <= 2 &&
          splitQuery[0].includes(county)
        );
      }
      return splitQuery.length === 1;
    })
    .map((option) => ({
      name: getTitleFromQuery(
        option.text,
        SearchType.CLOSED,
        option.flatType,
        i18n
      ),
      url: `/comprar/${getFlatTypeSlug(option.flatType, true)}/${option.query}`,
    }));
  return sortLinks(links);
};

const generateOfficesLinks = (
  i18n: I18nContextType,
  offices: IOffice[],
  county?: string,
  city?: string
): Link[] => {
  if (!county) {
    return computeOfficesByCountyArray(offices).map(([county, _]) => ({
      name: i18n.t('header.offices.officesInCounty', {
        county,
      }),
      url: `/${OFFICES_URL_PREFIX}/${slugifySearchQuery(
        county
      )}${SEARCH_BY_COUNTY_SUFFIX}`,
    }));
  }

  const links = offices
    .filter((office) => {
      if (city) {
        return office.city.toLowerCase().includes(city);
      }
      if (county) {
        return office.province
          .toLowerCase()
          .includes(county.replace(SEARCH_BY_COUNTY_SUFFIX, ''));
      }
      return true;
    })
    .map((office) => ({
      name: i18n.t('officeDetail.messages.title', {
        officeName: `${
          office.name.toLowerCase() === office.city.toLowerCase()
            ? ''
            : office.city + ' - '
        }${office.name}`,
      }),
      url: generateUrl(office),
    }));
  return sortLinks(links);
};

const generateSellHouseLinks = (
  i18n: I18nContextType,
  sellHouseOptions: SellHouseOption[],
  mode: LinksGridMode,
  county?: string,
  city?: string
): Link[] => {
  const links = sellHouseOptions
    .filter((option) => {
      const splitQuery = option.query.split('/');
      if (city) {
        return false;
      }
      if (county) {
        const minSplitQueryLength = mode === 'sell' ? 2 : 1;
        return (
          splitQuery.length >= minSplitQueryLength &&
          splitQuery.length <= 2 &&
          splitQuery[0].includes(county)
        );
      }
      return splitQuery.length === 1;
    })
    .map((option) => ({
      name: i18n.t('header.sellHouse.specificTitle', { text: option.text }),
      url: `/vender-casa/${option.query}`,
    }));
  return sortLinks(links);
};

const sortLinks = (links: Link[]): Link[] => {
  links.sort((a, b) => {
    const lengthDifference = a.url.split('/').length - b.url.split('/').length;
    if (lengthDifference !== 0) {
      return lengthDifference;
    }
    return a.name.localeCompare(b.name);
  });
  return links;
};
